import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import {
  Container,
  Grid,
  Tab,
  Tabs,
  Table,
  TablePagination,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Box,
  CardContent,
  Button,
  Chip,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import ButtonBlue from "../../shared/general/ButtonBlue";
import ButtonOrange from "../../shared/general/ButtonOrange";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditIcon from "@mui/icons-material/Edit";
import UploadIcon from "@mui/icons-material/Upload";
import ClearIcon from "@mui/icons-material/Clear";
import CardStyle from "../../shared/general/Card";

import DialogAddFamily from "./DialogAddFamily";
import ConfirmDialog from "./confirm-dialog";

import {
  getAllExpense,
  updateStatusExpense,
} from "../../../../actions/expense";
import dayjs from "dayjs";
import utils from "../../../../utils";
import AnimationBackdrop from "../../shared/general/Backdrop";

const StyledRoot = styled("div")({
  backgroundColor: "#FFFFFF !important",
  "& .MuiContainer-root": {
    paddingBottom: 24,
  },
  "& .head": {
    paddingTop: 8,
    marginBottom: 24,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  "& .head-2": {
    paddingTop: 8,
    marginBottom: 24,
    marginTop: 36,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  "& .wrap-sum-amount": {
    marginBottom: 16,
  },
});

const StyledDivTab = styled("div")(({ theme }) => ({
  width: "100%",
  padding: "0 8px",
  marginBottom: 16,
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  fontSize: 16,
  "& .MuiBadge-badge": {
    right: -16,
    top: 12,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
    backgroundColor: "#e46a76",
  },
}));

const StyledCard = styled(Card)({
  padding: "16px 32px",
  boxShadow: "rgb(90 114 123 / 11%) 0px 7px 30px 0px",
  transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  borderRadius: 20,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  textAlign: "center",
  ["@media only screen and (min-width:768px)"]: {
    height: 104,
    flexDirection: "column-reverse",
  },
  ["@media only screen and (min-width:992px)"]: {
    height: 80,
    flexDirection: "row",
  },
  "& .MuiTypography-h6": {
    fontSize: 22,
    lineHeight: 1.25,
  },
  "& .MuiTypography-body2": {
    fontSize: 16,
  },
  "&.expense": {
    backgroundColor: "#d0fce0",
    "& .MuiTypography-root": {
      color: "#045966",
    },
  },
  "&.advance": {
    backgroundColor: "#ff563029",
    "& .MuiTypography-root": {
      color: "#b71d18",
    },
  },
  "& .MuiCardContent-root": {
    padding: 24,
  },
});

const StyledTable = styled(Table)({
  "& .MuiTableCell-root": {
    textAlign: "center",
    "&.nothing": {
      padding: 32,
    },
  },
  "& .tableCellHead": {
    border: "none",
    backgroundColor: "#f3f6f8",
  },
  "& .tableCellHead:first-of-type": {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  "& .cellAction": {
    width: 48,
    padding: "0px 0px 0px 4px",
  },
  "& .tableCellHead-document-no": {
    width: 140,
  },
  "& .tableCellHead-document-name": {
    textAlign: "left",
    //width: 250,
  },
  "& .tableCellHead-document-amount": {
    width: 120,
  },
  "& .tableCellHead-document-status": {
    width: 140,
  },
  "& .tableCellHead:last-of-type": {
    paddingRight: 24,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
  "& .tableCellBody-document-name": {
    textAlign: "left",
  },

  "& .tableCellWeekend": {
    color: "#959595",
    fontStyle: "oblique",
  },
  "& .cellStatusAction": {
    textAlign: "left",
    "& .wrap-status": {
      display: "flex",
      "& .item": {
        marginRight: 4,
      },
    },
  },
  "& .cellRequest": {},
  "& .cellStartStop": {
    minWidth: 80,
    "& .MuiButtonBase-root": {
      marginRight: 2,
    },
  },
  "& .rowLink": {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgb(243, 246, 248, 0.5)",
    },
  },
});

const StyledChipTab = styled(Chip)({
  display: "flex",
  flexDirection: "column",
  width: 20,
  height: 20,
  marginLeft: 5,
  backgroundColor: "orangered",
  color: "#fff",
  "& .MuiChip-label": {
    display: "block",
    whiteSpace: "normal",
    textAlign: "center",
  },
});

const StyledChipStatus = styled(Chip)({
  display: "flex",
  flexDirection: "column",
  height: 24,
  width: 140,
  "& .MuiChip-label": {
    display: "block",
    whiteSpace: "normal",
    textAlign: "center",
  },
  "&.Draft-status": {
    color: "#455a64",
    borderColor: "#fafafa",
  },
  "&.Waiting-status": {
    color: "#7c4dff",
    borderColor: "#7c4dff",
    backgroundColor: "#ede7f6",
  },
  "&.WaitingForReview-status": {
    color: "#ff4081",
    borderColor: "#ff4081",
    backgroundColor: "#FFF3FC",
  },
  "&.Processing-status": {
    color: "#007AFE",
    borderColor: "#007AFE",
    backgroundColor: "#e3f2fd",
  },
  "&.Holding-status": {
    color: "#ff6d00",
    borderColor: "#ff6d00",
    backgroundColor: "#fff8e1",
  },
  "&.Completed-status": {
    color: "#00c853",
    borderColor: "#00c853",
    backgroundColor: "#e8f5e9",
  },
  "&.Rejected-status": {
    color: "#d50000",
    borderColor: "#d50000",
    backgroundColor: "#ffebee",
  },
});

const EditButton = styled(Button)(({ variant, theme }) => ({
  color: "#ff6d00",
  backgroundColor: "transparent",
  border: "solid",
  borderColor: "#ff6d00",
  paddingLeft: 12,
  paddingRight: 12,
  paddingTop: 4,
  paddingBottom: 4,
  borderRadius: 8,
  "&:hover": {
    color: "#ffffff",
    backgroundColor: "#ff6d00",
  },
}));

const StyledTabs = styled(Tabs)({
  "& .Mui-selected": {
    color: "#007AFE !important",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#007AFE !important",
  },
  "& .fal": {
    marginRight: 8,
  },
});

const statusList = {
  StatusDraft: "แบบร่าง",
  StatusWaiting: "รออนุมัติ",
  StatusProcessing: "อยู่ระหว่างดำเนินการ",
  StatusHolding: "รอการแก้ไข",
  StatusCompleted: "เสร็จสมบูรณ์",
  StatusRejected: "ไม่อนุมัติ",
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function SubTable(props) {
  const { data, handleClick, ...other } = props;
  const { t, i18n } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <div>
      <TableContainer>
        <StyledTable aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell className={`tableCellHead tableCellHead-document-no`}>
                {t("DocumentId")}
              </TableCell>
              <TableCell
                className={`tableCellHead tableCellHead-document-name`}
              >
                {t("Title")}
              </TableCell>
              <TableCell
                className={`tableCellHead tableCellHead-document-amount`}
              >
                {t("Amount")}
              </TableCell>

              <TableCell
                className={`tableCellHead tableCellHead-document-status`}
              >
                {t("Status")}
              </TableCell>
            </TableRow>
          </TableHead>

          {data && data.length && data.length > 0 ? (
            <TableBody>
              {(rowsPerPage > 0
                ? data.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : data
              ).map((row, index) => (
                <TableRow
                  className="rowLink"
                  onClick={() => {
                    handleClick(row.idExpense, row.expenseType);
                  }}
                >
                  <TableCell>{row.documentId}</TableCell>
                  <TableCell className="tableCellBody-document-name">
                    {row.expenseName}
                  </TableCell>
                  <TableCell>
                    {utils.numberWithCommas(parseFloat(row.net))} {t("Baht")}
                  </TableCell>
                  <TableCell>
                    <StyledChipStatus
                      label={t(`${row.name}`)}
                      // variant="outlined"
                      className={`${row.name}-status`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableCell className="nothing" colSpan={4}>
                {t("No_Data")}
              </TableCell>
            </TableBody>
          )}
        </StyledTable>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[
          10,
          20,
          30,
          40,
          50,
          { label: "ทั้งหมด", value: -1 },
        ]}
        component="div"
        count={data && data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t("RowPerPage")}
      />
    </div>
  );
}

function SubTableWithButton(props) {
  const {
    data,
    textButton,
    iconButton,
    handleClick,
    dialog,
    dialogTitle,
    dialogDescription,
    ...other
  } = props;
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openDialog, setOpenDialog] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClickDialog = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <div>
      <TableContainer>
        <StyledTable aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell className={`tableCellHead tableCellHead-document-no`}>
                {t("DocumentId")}
              </TableCell>
              <TableCell
                className={`tableCellHead tableCellHead-document-name`}
              >
                {t("Title")}
              </TableCell>
              <TableCell
                className={`tableCellHead tableCellHead-document-amount`}
              >
                {t("Amount")}
              </TableCell>

              <TableCell
                className={`tableCellHead tableCellHead-document-status`}
              >
                {t("Status")}
              </TableCell>
              <TableCell
                className={`tableCellHead tableCellHead-document-status`}
              ></TableCell>
            </TableRow>
          </TableHead>

          {data && data.length && data.length > 0 ? (
            <TableBody>
              {(rowsPerPage > 0
                ? data.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : data
              ).map((row, index) => (
                <TableRow key={row.idExpense}>
                  <TableCell>{row.documentId}</TableCell>
                  <TableCell className="tableCellBody-document-name">
                    {row.expenseName}
                  </TableCell>
                  <TableCell>
                    {utils.numberWithCommas(parseFloat(row.net))} {t("Baht")}
                  </TableCell>
                  <TableCell>{t(`${row.name}`)}</TableCell>
                  <TableCell>
                    {dialog ? (
                      <div>
                        <EditButton
                          startIcon={iconButton}
                          onClick={handleClickDialog}
                        >
                          {textButton}
                        </EditButton>
                        <ConfirmDialog
                          id={row.idExpense}
                          title={dialogTitle}
                          description={dialogDescription}
                          open={openDialog}
                          handleOnClick={() => {
                            handleClick(row.idExpense, row.expenseTypeId);
                          }}
                          handleClose={handleCloseDialog}
                        ></ConfirmDialog>
                      </div>
                    ) : (
                      <EditButton
                        startIcon={iconButton}
                        onClick={() => {
                          handleClick(row.idExpense, row.expenseType);
                        }}
                      >
                        {textButton}
                      </EditButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableCell className="nothing" colSpan={5}>
                {t("No_Data")}
              </TableCell>
            </TableBody>
          )}
        </StyledTable>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[
          10,
          20,
          30,
          40,
          50,
          { label: "ทั้งหมด", value: -1 },
        ]}
        component="div"
        count={data && data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t("RowPerPage")}
      />
    </div>
  );
}

const ExpensePage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [numberOfList, setNumberOfList] = useState(0);
  const [statusTab, setStatusTab] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [state, setstate] = useState([]);
  const [open, setOpen] = useState(false);
  const [value, setValue] = React.useState(0);
  const { result: userProfile } = useSelector((state) => state.userProfile);
  const { result: expense } = useSelector((state) => state.expense);

  useEffect(() => {
    dispatch(getAllExpense());
  }, [userProfile]);

  const handleApproveTabsChange = (_, newValue) => {
    setStatusTab(newValue);
  };

  const renderListCount = () => {};

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  const handleView = (idExpense, expenseType) => {
    window.open(`/expense/${expenseType}/view/${idExpense}/page`, "_blank");
  };

  const handleEditDraft = (idExpense, expenseType) => {
    history.push(`/expense/${expenseType}/Draft/${idExpense}`);
  };

  const handleEdit = (idExpense, expenseType) => {
    if (idExpense) {
      history.push(`/expense/${expenseType}/Edit/${idExpense}`);
    }
  };

  const handleCancel = async (idExpense, expenseTypeId) => {
    let data = {
      date: dayjs(dayjs().toDate()).format("YYYY/MM/DD HH:mm"),
      status: 1,
      actionType: 2,
      comment: "",
      typeExpense: expenseTypeId,
    };
    if (userProfile && userProfile.idEmp) {
      let result = await dispatch(updateStatusExpense(idExpense, data));
      if (result && result.status === 200) {
        dispatch(getAllExpense());
      }
    }
  };

  return (
    <StyledRoot className="page">
      <Container maxWidth="lg">
        <div className="head">
          <Typography variant="h4">{t("Expense")}</Typography>
          <div>
            <ButtonBlue
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => history.push("/expense/create")}
            >
              {t("AddExpense")}
            </ButtonBlue>
          </div>
        </div>

        <CardStyle>
          {expense ? (
            <CardContent>
              <StyledDivTab>
                <StyledTabs
                  value={statusTab}
                  onChange={handleApproveTabsChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="basic tabs example"
                  centered
                >
                  <Tab
                    style={{ minWidth: 180 }}
                    label={
                      <StyledBadge color="secondary">
                        {t("All")}
                        {expense.All &&
                        expense.All.length &&
                        expense.All.length > 0 ? (
                          <StyledChipTab label={expense.All.length} />
                        ) : (
                          <span></span>
                        )}
                      </StyledBadge>
                    }
                    {...a11yProps(0)}
                  />
                  {"Draft" in expense && (
                    <Tab
                      style={{ minWidth: 180 }}
                      label={
                        <StyledBadge color="secondary">
                          {t("Draft")}
                          {expense.Draft &&
                          expense.Draft.length &&
                          expense.Draft.length > 0 ? (
                            <StyledChipTab label={expense.Draft.length} />
                          ) : (
                            <span></span>
                          )}
                        </StyledBadge>
                      }
                      {...a11yProps(1)}
                    />
                  )}
                  {"Waiting" in expense && (
                    <Tab
                      style={{ minWidth: 180 }}
                      label={
                        <StyledBadge color="secondary">
                          {t("Waiting")}
                          {expense.Waiting &&
                          expense.Waiting.length &&
                          expense.Waiting.length > 0 ? (
                            <StyledChipTab label={expense.Waiting.length} />
                          ) : (
                            <span></span>
                          )}
                        </StyledBadge>
                      }
                      {...a11yProps(2)}
                    />
                  )}
                  {"WaitingForReview" in expense && (
                    <Tab
                      style={{ minWidth: 180 }}
                      label={
                        <StyledBadge color="secondary">
                          {t("WaitingForReview")}
                          {expense.WaitingForReview &&
                          expense.WaitingForReview.length &&
                          expense.WaitingForReview.length > 0 ? (
                            <StyledChipTab
                              label={expense.WaitingForReview.length}
                            />
                          ) : (
                            <span></span>
                          )}
                        </StyledBadge>
                      }
                      {...a11yProps(3)}
                    />
                  )}
                  {"Processing" in expense && (
                    <Tab
                      style={{ minWidth: 180 }}
                      label={
                        <StyledBadge color="secondary">
                          {t("Processing")}
                          {expense.Processing &&
                          expense.Processing.length &&
                          expense.Processing.length > 0 ? (
                            <StyledChipTab label={expense.Processing.length} />
                          ) : (
                            <span></span>
                          )}
                        </StyledBadge>
                      }
                      {...a11yProps(4)}
                    />
                  )}
                  {"Holding" in expense && (
                    <Tab
                      style={{ minWidth: 180 }}
                      label={
                        <StyledBadge color="secondary">
                          {t("Holding")}
                          {expense.Holding &&
                          expense.Holding.length &&
                          expense.Holding.length > 0 ? (
                            <StyledChipTab label={expense.Holding.length} />
                          ) : (
                            <span></span>
                          )}
                        </StyledBadge>
                      }
                      {...a11yProps(5)}
                    />
                  )}
                  {"Completed" in expense && (
                    <Tab
                      style={{ minWidth: 180 }}
                      label={
                        <StyledBadge color="secondary">
                          {t("Completed")}
                          {expense.Completed &&
                          expense.Completed.length &&
                          expense.Completed.length > 0 ? (
                            <StyledChipTab label={expense.Completed.length} />
                          ) : (
                            <span></span>
                          )}
                        </StyledBadge>
                      }
                      {...a11yProps(6)}
                    />
                  )}
                  {"Rejected" in expense && (
                    <Tab
                      style={{ minWidth: 180 }}
                      label={
                        <StyledBadge color="secondary">
                          {t("Rejected")}
                          {expense.Rejected &&
                          expense.Rejected.length &&
                          expense.Rejected.length > 0 ? (
                            <StyledChipTab label={expense.Rejected.length} />
                          ) : (
                            <span></span>
                          )}
                        </StyledBadge>
                      }
                      {...a11yProps(7)}
                    />
                  )}
                </StyledTabs>
              </StyledDivTab>

              <Box className="wrap-table">
                <TabPanel value={statusTab} index={0}>
                  <SubTable
                    data={"All" in expense ? expense.All : null}
                    handleClick={handleView}
                  ></SubTable>
                </TabPanel>
                <TabPanel value={statusTab} index={1}>
                  <SubTableWithButton
                    data={"Draft" in expense ? expense.Draft : null}
                    textButton={t("Edit")}
                    iconButton={<EditIcon />}
                    handleClick={handleEditDraft}
                  ></SubTableWithButton>
                </TabPanel>
                <TabPanel value={statusTab} index={2}>
                  <SubTableWithButton
                    data={"Waiting" in expense ? expense.Waiting : null}
                    textButton={t("Cancle")}
                    iconButton={<ClearIcon />}
                    handleClick={handleCancel}
                    dialog={true}
                    dialogTitle={t("CancleFormDialogTitle")}
                    dialogDescription={t("CancleFormDialogDes")}
                  ></SubTableWithButton>
                </TabPanel>
                <TabPanel value={statusTab} index={3}>
                  <SubTable
                    data={
                      "WaitingForReview" in expense
                        ? expense.WaitingForReview
                        : null
                    }
                    handleClick={handleView}
                  ></SubTable>
                </TabPanel>
                <TabPanel value={statusTab} index={4}>
                  <SubTable
                    data={"Processing" in expense ? expense.Processing : null}
                    handleClick={handleView}
                  ></SubTable>
                </TabPanel>
                <TabPanel value={statusTab} index={5}>
                  <SubTableWithButton
                    data={"Holding" in expense ? expense.Holding : null}
                    textButton={t("Edit")}
                    iconButton={<EditIcon />}
                    handleClick={handleEdit}
                  ></SubTableWithButton>
                </TabPanel>
                <TabPanel value={statusTab} index={6}>
                  <SubTable
                    data={"Completed" in expense ? expense.Completed : null}
                    handleClick={handleView}
                  ></SubTable>
                </TabPanel>
                <TabPanel value={statusTab} index={7}>
                  <SubTable
                    data={"Rejected" in expense ? expense.Rejected : null}
                    handleClick={handleView}
                  ></SubTable>
                </TabPanel>
              </Box>
            </CardContent>
          ) : (
            <AnimationBackdrop open={true} />
          )}
        </CardStyle>

        {/* <div className="head-2">
          <Typography variant="h4">สิทธิครอบครัว</Typography>
          <div>
            <ButtonBlue
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => setOpen(true)}
            >
              เพิ่มสิทธิครอบครัว
            </ButtonBlue>
          </div>
        </div>
        <CardStyle>
          <CardContent>
            <Box className="wrap-table">
              <TableContainer>
                <StyledTable aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        className={`tableCellHead tableCellHead-document-no`}
                      >
                        ความสัมพันธ์
                      </TableCell>
                      <TableCell
                        className={`tableCellHead tableCellHead-document-name`}
                      >
                        ชื่อ-สกุล
                      </TableCell>
                      <TableCell
                        className={`tableCellHead tableCellHead-document-amount`}
                      >
                        เลขบัตรประชาชน
                      </TableCell>

                      <TableCell
                        className={`tableCellHead tableCellHead-document-status`}
                      >
                        {t("Status")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.length > 0 ? (
                      <div></div>
                    ) : (
                      <TableCell className="nothing" colSpan={4}>
                        ไม่พบรายการ
                      </TableCell>
                    )}
                  </TableBody>
                </StyledTable>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 20, 30, 40, 50, "ทั้งหมด"]}
                component="div"
                count={state.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </CardContent>
        </CardStyle>
        <DialogAddFamily open={open} handleCloseDialog={handleCloseDialog} /> */}
      </Container>
    </StyledRoot>
  );
};

export default ExpensePage;
